import { SUMMER_SALE } from '@/lib/constants';
import { useAuth } from '@/lib/AuthContext';
import { getSubscriptionInterval } from '@/services/subscription-interval.service';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';

const SUBSCRIPTION_INTERVAL_STALE_MS = 30 * 60 * 1000; // membership interval changes rarely
const WINDOW_TICK_MS = 60 * 1000; // re-check the sale window every minute so it flips without a reload

// Dismissing the banner hides it for the rest of that (local) day; it comes
// back the next day for the remainder of the sale.
const DISMISSED_STORAGE_KEY = 'summer-sale-banner-dismissed-on';

export interface SummerSaleBanner {
  /** Destination for the banner link, chosen by audience. */
  url: string;
  /**
   * Visual to render: 'classic' (logo + copy) until the final phase starts,
   * then 'countdown' (the "Final 72 Hours" timer design from WP post 1601603).
   */
  variant: 'classic' | 'countdown';
  /** Hide the banner for the rest of the day (persisted per browser). */
  dismiss: () => void;
}

function isWithinSaleWindow(now: number): boolean {
  return now >= SUMMER_SALE.START_MS && now <= SUMMER_SALE.END_MS;
}

function localDayKey(): string {
  return new Date().toDateString();
}

function isDismissedToday(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === localDayKey();
}

/**
 * Resolves the Summer Sale banner for the current (real, logged-in) user during
 * the sale window, or `null` when no banner should show. Audience rules:
 *   - pro members     → no banner (any billing interval — management rule)
 *   - annual members  → no banner
 *   - monthly members → banner linking to the annual-upgrade page (even if they
 *                       placed an order during the sale — management rule)
 *   - everyone else   → banner linking to the general sale page, unless they
 *                       placed a paid order since the sale cutoff (e.g. bought
 *                       Lifetime — they already purchased)
 * Dismissing via the X hides the banner for the rest of that local day.
 * (There are no anonymous users on Josephine, so that case isn't handled here.)
 *
 * The subscription-interval fetch is only enabled inside the window, so there's
 * no WordPress load before/after the sale.
 */
export function useSummerSaleBanner(): SummerSaleBanner | null {
  const { user, loading, membershipTier, membershipLoading } = useAuth();
  const isRealUser = !!user && !user.is_anonymous;

  const [withinWindow, setWithinWindow] = useState(() => isWithinSaleWindow(Date.now()));
  const [finalPhase, setFinalPhase] = useState(
    () => Date.now() >= SUMMER_SALE.FINAL_PHASE_START_MS,
  );
  const [dismissedToday, setDismissedToday] = useState(() => isDismissedToday());

  useEffect(() => {
    const tick = () => {
      setWithinWindow(isWithinSaleWindow(Date.now()));
      setFinalPhase(Date.now() >= SUMMER_SALE.FINAL_PHASE_START_MS); // countdown design flips on without a reload
      setDismissedToday(isDismissedToday()); // yesterday's dismissal expires without a reload
    };
    tick();
    const id = setInterval(tick, WINDOW_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const dismiss = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, localDayKey());
    }
    setDismissedToday(true);
  }, []);

  // `data` is `undefined` until the query resolves. We wait for isSuccess so we
  // never show the wrong link (or briefly show it to an annual member) before
  // we know their tier.
  const { data, isSuccess } = useQuery({
    queryKey: ['subscription-interval', user?.id],
    queryFn: getSubscriptionInterval,
    enabled: !loading && isRealUser && withinWindow,
    staleTime: SUBSCRIPTION_INTERVAL_STALE_MS,
  });

  if (!withinWindow || !isRealUser || !isSuccess || membershipLoading || dismissedToday) {
    return null;
  }

  const variant = finalPhase ? 'countdown' : 'classic';

  if (membershipTier === 'pro') return null; // pro members: no banner (any interval)
  if (data.hasLifetime) return null; // lifetime owners: no banner (their payment plan looks like a monthly sub)
  if (data.interval === 'year') return null; // annual members: no banner
  if (data.interval === 'month') return { url: SUMMER_SALE.UPGRADE_URL, variant, dismiss }; // monthly → upgrade to annual
  if (data.hasRecentPaidOrder) return null; // already bought during the sale
  return { url: SUMMER_SALE.URL, variant, dismiss }; // everyone else → general sale page
}
